import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Button,
  Box,
  Stack,
  Autocomplete,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material'
import { usersApi } from '@/api/users'
import type { CreateGroupData, UpdateGroupData, StudentGroup, User } from '@/types'
import { displayErrorMessage, getErrorMessage, getListItemValidationErrors, getValidationErrors } from '@/utils/error'
import { describeMembersError, getMemberLabel } from '@/components/groups/memberSelection'

const createGroupSchema = (t: (key: string) => string) => z.object({
  name: z
    .string()
    .min(1, t('groups.validation.nameRequired'))
    .max(255, t('groups.validation.maxLength')),
  description: z
    .string()
    .max(1000, t('groups.validation.descriptionMaxLength'))
    .optional()
    .or(z.literal('')),
  member_ids: z.array(z.number()).optional(),
})

interface GroupFormProps {
  initialValues?: StudentGroup
  /** Ошибку, проброшенную из onSubmit, форма показывает сама */
  onSubmit: (data: CreateGroupData | UpdateGroupData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function GroupForm({
  initialValues,
  onSubmit,
  onCancel,
  isLoading,
}: GroupFormProps) {
  const { t } = useTranslation()
  const isEdit = !!initialValues
  const [students, setStudents] = useState<User[]>([])
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  /** Ученики, которых API отклонил при последней отправке */
  const [rejectedIds, setRejectedIds] = useState<number[]>([])

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CreateGroupData | UpdateGroupData>({
    resolver: zodResolver(createGroupSchema(t)),
    defaultValues: initialValues
      ? { name: initialValues.name, description: initialValues.description ?? '' }
      : { name: '', description: '', member_ids: [] },
  })

  const memberIds = watch('member_ids') ?? []

  useEffect(() => {
    if (!isEdit) {
      setStudentsLoading(true)
      // Все свои ученики с активным договором (по умолчанию у списка
      // учителя), а не первая страница: участниками могут быть только они
      usersApi
        .listAllStudents()
        .then(setStudents)
        .catch((err: unknown) => {
          setSubmitError(displayErrorMessage(getErrorMessage(err), t('groups.errorLoadStudents')))
        })
        .finally(() => setStudentsLoading(false))
    }
  }, [isEdit, t])

  const handleFormSubmit = async (data: CreateGroupData | UpdateGroupData) => {
    const payload = { ...data } as CreateGroupData & UpdateGroupData
    if (isEdit || !payload.member_ids?.length) {
      delete payload.member_ids
    }
    // Ученики в порядке отправки: по нему API называет отклонённых (member_ids.N)
    const submittedMembers = (payload.member_ids ?? [])
      .map((id) => students.find((s) => s.id === id))
      .filter((user): user is User => user !== undefined)

    setSubmitError(null)
    setRejectedIds([])
    try {
      await onSubmit(payload as CreateGroupData | UpdateGroupData)
    } catch (err: unknown) {
      const fieldErrors = getValidationErrors(err)
      for (const field of ['name', 'description'] as const) {
        const message = fieldErrors?.[field]?.[0]
        if (message) {
          setError(field, { type: 'server', message })
        }
      }
      const rejected = getListItemValidationErrors(fieldErrors, 'member_ids')
      setRejectedIds(
        (rejected?.indexes ?? [])
          .map((index) => payload.member_ids?.[index])
          .filter((id): id is number => typeof id === 'number'),
      )
      setSubmitError(
        describeMembersError(
          err,
          'member_ids',
          submittedMembers,
          t,
          isEdit ? 'groups.errorUpdate' : 'groups.errorCreate',
        ),
      )
    }
  }

  const selectedUsers = students.filter((s) => memberIds.includes(s.id))

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3} sx={{ pt: 1 }}>
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('groups.form.name')}
              placeholder={t('groups.form.namePlaceholder')}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              required
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('groups.form.description')}
              placeholder={t('groups.form.descriptionPlaceholder')}
              error={!!errors.description}
              helperText={errors.description?.message}
              fullWidth
              multiline
              rows={4}
            />
          )}
        />

        {!isEdit && (
          <Controller
            name="member_ids"
            control={control}
            render={() => (
              <Autocomplete
                multiple
                options={students}
                getOptionLabel={getMemberLabel}
                value={selectedUsers}
                onChange={(_, newValue) => {
                  setValue('member_ids', newValue.map((u) => u.id))
                  setRejectedIds((ids) => ids.filter((id) => newValue.some((u) => u.id === id)))
                }}
                loading={studentsLoading}
                renderValue={(value, getItemProps) =>
                  value.map((student, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return (
                      <Chip
                        key={key}
                        {...itemProps}
                        label={getMemberLabel(student)}
                        color={rejectedIds.includes(student.id) ? 'error' : 'default'}
                      />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('groups.form.members')}
                    placeholder={t('groups.form.membersPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {studentsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isEdit ? t('common.save') : t('groups.create')}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}
